import { useLogin } from "@refinedev/core";
import { Hexagon, Lock, Sparkles, Zap } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { generatePKCE, generateState } from "@/lib/oidc";
import { getOIDCConfig } from "@/services/auth";

export function LoginPage() {
  const { mutate: login, isLoading } = useLogin();
  const [account, setAccount] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [oidcLoading, setOidcLoading] = React.useState(false);
  const [facetEnabled, setFacetEnabled] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    getOIDCConfig()
      .then((cfg) => setFacetEnabled(cfg.enabled))
      .catch(() => setFacetEnabled(false));
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    login({ username: account, password });
  };

  const [oidcError, setOidcError] = React.useState<string | null>(null);

  const handleFacetLogin = async () => {
    setOidcError(null);
    setOidcLoading(true);
    try {
      const cfg = await getOIDCConfig({ forLogin: true });
      if (!cfg.enabled) {
        // Facet not configured: keep using dev password login
        login({ username: "dev", password: "dev" });
        return;
      }

      const redirectUri = cfg.redirect_uri || `${window.location.origin}/auth/callback`;
      const { codeVerifier, codeChallenge } = await generatePKCE();
      const state = generateState();

      sessionStorage.setItem("facet_oidc_state", state);
      sessionStorage.setItem("facet_oidc_code_verifier", codeVerifier);
      sessionStorage.setItem("facet_oidc_redirect_uri", redirectUri);

      const url = new URL(cfg.authorize_url);
      url.searchParams.set("client_id", cfg.client_id);
      url.searchParams.set("redirect_uri", redirectUri);
      url.searchParams.set("response_type", "code");
      url.searchParams.set("scope", cfg.scope);
      url.searchParams.set("state", state);
      url.searchParams.set("code_challenge", codeChallenge);
      url.searchParams.set("code_challenge_method", "S256");
      window.location.href = url.toString();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setOidcError(`Facet 登录初始化失败：${message}`);
    } finally {
      setOidcLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-950 p-4">
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-24px) rotate(6deg); }
        }
        @keyframes floatSlow {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-16px) rotate(-6deg); }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.35; transform: scale(1); }
          50% { opacity: 0.55; transform: scale(1.05); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-float { animation: float 7s ease-in-out infinite; }
        .animate-float-slow { animation: floatSlow 9s ease-in-out infinite; }
        .animate-pulse-glow { animation: pulseGlow 5s ease-in-out infinite; }
        .animate-fade-in-up { animation: fadeInUp 0.7s ease-out forwards; }
      `}</style>

      {/* Gradient background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-900/40 via-slate-950 to-slate-950" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-violet-900/20 via-transparent to-transparent" />

      {/* Animated blobs */}
      <div
        className="animate-float-slow absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-indigo-600/20 blur-3xl"
        style={{ animationDelay: "0s" }}
      />
      <div
        className="animate-float absolute -right-32 bottom-1/4 h-96 w-96 rounded-full bg-violet-600/20 blur-3xl"
        style={{ animationDelay: "1s" }}
      />
      <div
        className="animate-pulse-glow absolute left-1/3 top-1/3 h-64 w-64 rounded-full bg-cyan-600/10 blur-3xl"
        style={{ animationDelay: "2s" }}
      />

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <Card className="animate-fade-in-up relative w-full max-w-md border-white/10 bg-white/5 shadow-2xl backdrop-blur-xl">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-cyan-500" />

        <CardHeader className="space-y-4 text-center pb-2">
          {/* Logo */}
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg shadow-indigo-500/25 transition-transform duration-500 hover:scale-105 hover:rotate-3">
            <Hexagon className="h-9 w-9 text-white" strokeWidth={1.5} />
          </div>

          <div className="space-y-1">
            <CardTitle className="flex items-center justify-center gap-2 text-3xl font-bold tracking-tight text-white">
              罢歌亭
              <Sparkles className="h-5 w-5 text-violet-400" />
            </CardTitle>
            <CardDescription className="text-base text-slate-400">
              DebugPavilion · AI 原生零代码应用平台
            </CardDescription>
          </div>
        </CardHeader>

        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-5 pt-4">
            <div className="space-y-2">
              <label htmlFor="account" className="text-sm font-medium text-slate-300">
                账号
              </label>
              <Input
                id="account"
                placeholder="请输入账号 / 邮箱"
                value={account}
                onChange={(e) => setAccount(e.target.value)}
                required
                className="border-white/10 bg-white/5 text-white placeholder:text-slate-500 focus-visible:ring-indigo-500/50 focus-visible:ring-offset-0"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-slate-300">
                密码
              </label>
              <Input
                id="password"
                type="password"
                placeholder="请输入密码"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="border-white/10 bg-white/5 text-white placeholder:text-slate-500 focus-visible:ring-indigo-500/50 focus-visible:ring-offset-0"
              />
            </div>

            <div className="rounded-lg border border-indigo-500/20 bg-indigo-500/10 p-3 text-xs text-indigo-200">
              <span className="flex items-center gap-1.5">
                <Zap className="h-3.5 w-3.5" />
                {facetEnabled === null
                  ? "正在检测登录方式..."
                  : facetEnabled
                    ? "已接入 Facet：账号密码需为 Facet 用户，或点击统一登录"
                    : "开发模式：任意账号密码均可登录"}
              </span>
            </div>
            {oidcError && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
                {oidcError}
              </div>
            )}
          </CardContent>

          <CardFooter className="flex flex-col gap-3">
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/25 transition-all duration-300 hover:from-indigo-400 hover:to-violet-500 hover:shadow-indigo-500/40 hover:-translate-y-0.5"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  登录中...
                </span>
              ) : (
                "登录"
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              disabled={oidcLoading}
              onClick={() => void handleFacetLogin()}
              className="w-full border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white"
            >
              {oidcLoading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                  跳转中...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  使用 Facet 统一登录
                </span>
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>

      {/* Bottom tagline */}
      <div className="absolute bottom-6 text-center text-xs text-slate-600">
        可视化搭建 · AI 生成 · 数据驱动 · 权限隔离
      </div>
    </div>
  );
}
